import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { apiDelete, apiGet, apiPost, apiPut, qs } from '@/app/core/http/apiClient'
import { setCustomers } from '@/data/accounting/customers'
import type {
  ArAgingReport,
  CreditNote,
  CreditNoteLineItem,
  Customer,
  CustomerAddress,
  DebitNote,
  FinancialStatement,
  Invoice,
  InvoiceLineItem,
  JournalEntry,
  JournalLine,
  LedgerAccount,
  PaymentMethod,
  TrialBalance,
} from '@/data/accounting/types'

const LEGAL_ENTITY_ID = 'a0000000-0000-0000-0000-0000000000e1'

/* ── Mutation input shapes ── */

export interface CreateInvoiceInput {
  customerId: string
  legalEntityId?: string
  issueDate: string
  dueDate: string
  currency: string
  placeOfSupply?: string | null
  notes?: string | null
  lineItems: Array<Pick<InvoiceLineItem, 'description' | 'hsnCode' | 'quantity' | 'unitOfMeasure' | 'unitPrice' | 'taxRate' | 'skuId'>>
}

export interface RecordPaymentInput {
  amount: number
  currency: string
  paymentDate: string
  paymentMethod: PaymentMethod
  reference?: string | null
  notes?: string | null
}

export interface CustomerInput {
  name: string
  email?: string | null
  phone?: string | null
  gstin?: string | null
  billingAddress?: CustomerAddress | null
  shippingAddress?: CustomerAddress | null
  creditLimit?: number | null
  paymentTermsDays?: number | null
  defaultCurrency?: string | null
}

export interface CreateJournalEntryInput {
  entryDate: string
  description: string
  currency: string
  lines: Array<Pick<JournalLine, 'accountId' | 'debitAmount' | 'creditAmount' | 'description'>>
}

export interface CreateLedgerAccountInput {
  accountCode: string
  name: string
  description?: string | null
  accountType: string
  currency: string
  parentAccountId?: string | null
  allowDirectPosting?: boolean
}

export interface CreateCreditNoteInput {
  originalInvoiceId: string
  issueDate: string
  reason: string
  lineItems: Array<Pick<CreditNoteLineItem, 'description' | 'amount' | 'taxAmount'>>
}

export interface CreateDebitNoteInput {
  originalInvoiceId: string
  issueDate: string
  reason: string
  amount: number
  taxAmount: number
}

interface AccountingContextValue {
  invoices: Invoice[]
  customers: Customer[]
  ledgerAccounts: LedgerAccount[]
  journalEntries: JournalEntry[]
  creditNotes: CreditNote[]
  debitNotes: DebitNote[]
  loading: boolean
  error: string | null
  refetchAll: () => Promise<void>

  // Invoices
  createInvoice: (input: CreateInvoiceInput) => Promise<Invoice>
  sendInvoice: (id: string) => Promise<void>
  cancelInvoice: (id: string, reason: string) => Promise<void>
  recordPayment: (id: string, input: RecordPaymentInput) => Promise<void>

  // Customers
  createCustomer: (input: CustomerInput) => Promise<Customer>
  updateCustomer: (id: string, input: CustomerInput) => Promise<void>
  deactivateCustomer: (id: string) => Promise<void>
  reactivateCustomer: (id: string) => Promise<void>

  // Ledger accounts
  createLedgerAccount: (input: CreateLedgerAccountInput) => Promise<LedgerAccount>
  freezeAccount: (id: string) => Promise<void>
  unfreezeAccount: (id: string) => Promise<void>
  deactivateAccount: (id: string) => Promise<void>
  reactivateAccount: (id: string) => Promise<void>

  // Journal entries
  createJournalEntry: (input: CreateJournalEntryInput) => Promise<JournalEntry>
  postJournalEntry: (id: string) => Promise<void>
  reverseJournalEntry: (id: string) => Promise<void>
  deleteJournalEntry: (id: string) => Promise<void>

  // Credit & debit notes
  createCreditNote: (input: CreateCreditNoteInput) => Promise<CreditNote>
  issueCreditNote: (id: string) => Promise<void>
  applyCreditNote: (id: string, invoiceId: string, amount: number) => Promise<void>
  cancelCreditNote: (id: string, reason: string) => Promise<void>
  createDebitNote: (input: CreateDebitNoteInput) => Promise<DebitNote>
  issueDebitNote: (id: string) => Promise<void>
  cancelDebitNote: (id: string, reason: string) => Promise<void>

  // Reports
  getProfitAndLoss: (startDate: string, endDate: string, currency?: string) => Promise<FinancialStatement>
  getBalanceSheet: (asOfDate: string, currency?: string) => Promise<FinancialStatement>
  getTrialBalance: (asOfDate: string, currency?: string) => Promise<TrialBalance>
  getArAging: (asOfDate: string) => Promise<ArAgingReport>
}

const AccountingContext = createContext<AccountingContextValue | null>(null)

export function AccountingProvider({ children }: { children: ReactNode }) {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [customers, setCustomersState] = useState<Customer[]>([])
  const [ledgerAccounts, setLedgerAccounts] = useState<LedgerAccount[]>([])
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([])
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([])
  const [debitNotes, setDebitNotes] = useState<DebitNote[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [inv, cust, ledger, journals, credits, debits] = await Promise.all([
        apiGet<Invoice[]>('/invoices'),
        apiGet<Customer[]>('/customers'),
        apiGet<LedgerAccount[]>('/ledger-accounts'),
        apiGet<JournalEntry[]>('/journal-entries'),
        apiGet<CreditNote[]>('/credit-notes'),
        apiGet<DebitNote[]>('/debit-notes'),
      ])
      setInvoices(inv ?? [])
      const customerList = cust ?? []
      setCustomersState(customerList)
      setCustomers(customerList)
      setLedgerAccounts(ledger ?? [])
      setJournalEntries(journals ?? [])
      setCreditNotes(credits ?? [])
      setDebitNotes(debits ?? [])
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load accounting data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refetchAll()
  }, [refetchAll])

  /* ── Invoices ── */
  const createInvoice = useCallback<AccountingContextValue['createInvoice']>(
    async (input) => {
      const created = await apiPost<Invoice>('/invoices', {
        customerId: input.customerId,
        legalEntityId: input.legalEntityId ?? LEGAL_ENTITY_ID,
        issueDate: input.issueDate,
        dueDate: input.dueDate,
        currency: input.currency,
        placeOfSupply: input.placeOfSupply || null,
        notes: input.notes || null,
        lineItems: input.lineItems.map((l) => ({
          description: l.description,
          hsnCode: l.hsnCode || null,
          quantity: l.quantity,
          unitOfMeasure: l.unitOfMeasure || 'EA',
          unitPrice: l.unitPrice,
          taxRate: l.taxRate,
          skuId: l.skuId || null,
        })),
      })
      await refetchAll()
      return created
    },
    [refetchAll],
  )

  const sendInvoice = useCallback(async (id: string) => {
    await apiPost(`/invoices/${id}/send`)
    await refetchAll()
  }, [refetchAll])

  const cancelInvoice = useCallback(async (id: string, reason: string) => {
    await apiPost(`/invoices/${id}/cancel${qs({ reason })}`)
    await refetchAll()
  }, [refetchAll])

  const recordPayment = useCallback(async (id: string, input: RecordPaymentInput) => {
    await apiPost(`/invoices/${id}/payments`, input)
    await refetchAll()
  }, [refetchAll])

  /* ── Customers ── */
  const createCustomer = useCallback<AccountingContextValue['createCustomer']>(
    async (input) => {
      const created = await apiPost<Customer>('/customers', input)
      await refetchAll()
      return created
    },
    [refetchAll],
  )

  const updateCustomer = useCallback(async (id: string, input: CustomerInput) => {
    await apiPut(`/customers/${id}`, input)
    await refetchAll()
  }, [refetchAll])

  const deactivateCustomer = useCallback(async (id: string) => {
    await apiPost(`/customers/${id}/deactivate`)
    await refetchAll()
  }, [refetchAll])

  const reactivateCustomer = useCallback(async (id: string) => {
    await apiPost(`/customers/${id}/reactivate`)
    await refetchAll()
  }, [refetchAll])

  /* ── Ledger accounts ── */
  const createLedgerAccount = useCallback<AccountingContextValue['createLedgerAccount']>(
    async (input) => {
      const created = await apiPost<LedgerAccount>('/ledger-accounts', {
        ...input,
        allowDirectPosting: input.allowDirectPosting ?? true,
      })
      await refetchAll()
      return created
    },
    [refetchAll],
  )

  const freezeAccount = useCallback(async (id: string) => {
    await apiPost(`/ledger-accounts/${id}/freeze`)
    await refetchAll()
  }, [refetchAll])

  const unfreezeAccount = useCallback(async (id: string) => {
    await apiPost(`/ledger-accounts/${id}/unfreeze`)
    await refetchAll()
  }, [refetchAll])

  const deactivateAccount = useCallback(async (id: string) => {
    await apiPost(`/ledger-accounts/${id}/deactivate`)
    await refetchAll()
  }, [refetchAll])

  const reactivateAccount = useCallback(async (id: string) => {
    await apiPost(`/ledger-accounts/${id}/reactivate`)
    await refetchAll()
  }, [refetchAll])

  /* ── Journal entries ── */
  const createJournalEntry = useCallback<AccountingContextValue['createJournalEntry']>(
    async (input) => {
      const created = await apiPost<JournalEntry>('/journal-entries', {
        entryDate: input.entryDate,
        description: input.description,
        currency: input.currency,
        lines: input.lines.map((l) => ({
          accountId: l.accountId,
          debitAmount: l.debitAmount || 0,
          creditAmount: l.creditAmount || 0,
          description: l.description || null,
        })),
      })
      await refetchAll()
      return created
    },
    [refetchAll],
  )

  const postJournalEntry = useCallback(async (id: string) => {
    await apiPost(`/journal-entries/${id}/post`)
    await refetchAll()
  }, [refetchAll])

  const reverseJournalEntry = useCallback(async (id: string) => {
    await apiPost(`/journal-entries/${id}/reverse`)
    await refetchAll()
  }, [refetchAll])

  const deleteJournalEntry = useCallback(async (id: string) => {
    await apiDelete(`/journal-entries/${id}`)
    await refetchAll()
  }, [refetchAll])

  /* ── Credit & debit notes ── */
  const createCreditNote = useCallback<AccountingContextValue['createCreditNote']>(
    async (input) => {
      const created = await apiPost<CreditNote>('/credit-notes', input)
      await refetchAll()
      return created
    },
    [refetchAll],
  )

  const issueCreditNote = useCallback(async (id: string) => {
    await apiPost(`/credit-notes/${id}/issue`)
    await refetchAll()
  }, [refetchAll])

  const applyCreditNote = useCallback(async (id: string, invoiceId: string, amount: number) => {
    await apiPost(`/credit-notes/${id}/apply${qs({ invoiceId, amount })}`)
    await refetchAll()
  }, [refetchAll])

  const cancelCreditNote = useCallback(async (id: string, reason: string) => {
    await apiPost(`/credit-notes/${id}/cancel${qs({ reason })}`)
    await refetchAll()
  }, [refetchAll])

  const createDebitNote = useCallback<AccountingContextValue['createDebitNote']>(
    async (input) => {
      const created = await apiPost<DebitNote>('/debit-notes', input)
      await refetchAll()
      return created
    },
    [refetchAll],
  )

  const issueDebitNote = useCallback(async (id: string) => {
    await apiPost(`/debit-notes/${id}/issue`)
    await refetchAll()
  }, [refetchAll])

  const cancelDebitNote = useCallback(async (id: string, reason: string) => {
    await apiPost(`/debit-notes/${id}/cancel${qs({ reason })}`)
    await refetchAll()
  }, [refetchAll])

  /* ── Reports ── */
  const getProfitAndLoss = useCallback(
    (startDate: string, endDate: string, currency?: string) =>
      apiGet<FinancialStatement>(`/financial-reports/profit-and-loss${qs({ startDate, endDate, currency })}`),
    [],
  )
  const getBalanceSheet = useCallback(
    (asOfDate: string, currency?: string) =>
      apiGet<FinancialStatement>(`/financial-reports/balance-sheet${qs({ asOfDate, currency })}`),
    [],
  )
  const getTrialBalance = useCallback(
    (asOfDate: string, currency?: string) =>
      apiGet<TrialBalance>(`/financial-reports/trial-balance${qs({ asOfDate, currency })}`),
    [],
  )
  const getArAging = useCallback(
    (asOfDate: string) => apiGet<ArAgingReport>(`/financial-reports/ar-aging${qs({ asOfDate })}`),
    [],
  )

  const value = useMemo<AccountingContextValue>(
    () => ({
      invoices,
      customers,
      ledgerAccounts,
      journalEntries,
      creditNotes,
      debitNotes,
      loading,
      error,
      refetchAll,
      createInvoice,
      sendInvoice,
      cancelInvoice,
      recordPayment,
      createCustomer,
      updateCustomer,
      deactivateCustomer,
      reactivateCustomer,
      createLedgerAccount,
      freezeAccount,
      unfreezeAccount,
      deactivateAccount,
      reactivateAccount,
      createJournalEntry,
      postJournalEntry,
      reverseJournalEntry,
      deleteJournalEntry,
      createCreditNote,
      issueCreditNote,
      applyCreditNote,
      cancelCreditNote,
      createDebitNote,
      issueDebitNote,
      cancelDebitNote,
      getProfitAndLoss,
      getBalanceSheet,
      getTrialBalance,
      getArAging,
    }),
    [
      invoices, customers, ledgerAccounts, journalEntries, creditNotes, debitNotes, loading, error, refetchAll,
      createInvoice, sendInvoice, cancelInvoice, recordPayment,
      createCustomer, updateCustomer, deactivateCustomer, reactivateCustomer,
      createLedgerAccount, freezeAccount, unfreezeAccount, deactivateAccount, reactivateAccount,
      createJournalEntry, postJournalEntry, reverseJournalEntry, deleteJournalEntry,
      createCreditNote, issueCreditNote, applyCreditNote, cancelCreditNote,
      createDebitNote, issueDebitNote, cancelDebitNote,
      getProfitAndLoss, getBalanceSheet, getTrialBalance, getArAging,
    ],
  )

  return <AccountingContext.Provider value={value}>{children}</AccountingContext.Provider>
}

export function useAccounting(): AccountingContextValue {
  const ctx = useContext(AccountingContext)
  if (!ctx) throw new Error('useAccounting must be used within AccountingProvider')
  return ctx
}
